import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { cn } from '../utils/cn';
import { useAuthStore } from '../stores/authStore';

interface SubscriptionData {
  subscription_status: string;
  current_period_end: number | null;
}

const SubscriptionStatus: React.FC = () => {
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState<string | null>(null);
  const { isAuthenticated, isAdmin } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) return;
    
    const fetchSubscription = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const { data, error } = await supabase
          .from('stripe_user_subscriptions')
          .select('subscription_status, current_period_end')
          .maybeSingle();
        
        if (error) throw error;
        
        setSubscription(data);
      } catch (err) {
        console.error('Error fetching subscription:', err);
        setError('サブスクリプション情報の取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };
    
    fetchSubscription();
  }, [isAuthenticated]);

  if (!isAuthenticated || loading || isAdmin) return null;

  // サブスクリプションがない場合
  if (!subscription || subscription.subscription_status === 'not_started') {
    return (
      <div className="fixed bottom-4 left-4 max-w-sm">
        <div className="bg-yellow-50 p-4 rounded-lg shadow-lg">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-1" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-yellow-800">
                サブスクリプションが未登録です
              </p>
              <p className="text-xs text-yellow-600 mt-1">
                アプリの全機能を利用するにはサブスクリプションに登録してください
              </p>
              <button
                onClick={() => navigate('/subscription')}
                className="mt-2 text-xs font-medium text-yellow-800 bg-yellow-200 hover:bg-yellow-300 px-2 py-1 rounded"
              >
                サブスクリプションを登録する
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // アクティブなサブスクリプションがある場合
  if (subscription.subscription_status === 'active' || subscription.subscription_status === 'trialing') {
    const endDate = subscription.current_period_end 
      ? new Date(subscription.current_period_end * 1000).toLocaleDateString('ja-JP')
      : '不明';
      
    return (
      <div className="fixed bottom-4 left-4 max-w-sm">
        <div className="bg-green-50 p-4 rounded-lg shadow-lg">
          <div className="flex items-start space-x-3">
            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-1" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-green-800">
                サブスクリプション有効
              </p>
              <p className="text-xs text-green-600 mt-1">
                次回更新日: {endDate}
              </p>
              <button
                onClick={() => navigate('/subscription')}
                className="mt-2 text-xs font-medium text-green-800 bg-green-200 hover:bg-green-300 px-2 py-1 rounded"
              >
                サブスクリプション管理
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // その他のステータス（past_due, canceled, unpaid など）
  return (
    <div className="fixed bottom-4 left-4 max-w-sm">
      <div className={cn(
        "p-4 rounded-lg shadow-lg",
        subscription.subscription_status === 'trialing' ? "bg-blue-50" :
        subscription.subscription_status === 'past_due' ? "bg-orange-50" :
        "bg-red-50"
      )}>
        <div className="flex items-start space-x-3">
          <AlertCircle className={cn(
            "h-5 w-5 flex-shrink-0 mt-1",
            subscription.subscription_status === 'trialing' ? "text-blue-500" :
            subscription.subscription_status === 'past_due' ? "text-orange-500" :
            "text-red-500"
          )} />
          <div className="flex-1 min-w-0">
            <p className={cn(
              "text-sm font-medium",
              subscription.subscription_status === 'trialing' ? "text-blue-800" :
              subscription.subscription_status === 'past_due' ? "text-orange-800" :
              "text-red-800"
            )}>
              {subscription.subscription_status === 'trialing' ? 'トライアル期間中' :
               subscription.subscription_status === 'past_due' ? '支払いが遅延しています' :
               subscription.subscription_status === 'canceled' ? 'サブスクリプションがキャンセルされました' :
               subscription.subscription_status === 'unpaid' ? '支払いが確認できません' :
               'サブスクリプションに問題があります'}
            </p>
            <button
              onClick={() => navigate('/subscription')}
              className={cn(
                "mt-2 text-xs font-medium px-2 py-1 rounded",
                subscription.subscription_status === 'trialing' ? "text-blue-800 bg-blue-200 hover:bg-blue-300" :
                subscription.subscription_status === 'past_due' ? "text-orange-800 bg-orange-200 hover:bg-orange-300" :
                "text-red-800 bg-red-200 hover:bg-red-300"
              )}
            >
              サブスクリプション管理
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionStatus;